/** Details drawer for a selected watercourse measure feature. */

import { memo, useEffect, useMemo, useState } from "react";

import { useLanguageContext } from "../hooks/useLanguage";
import { renderEmphasis } from "../utils/emphasis";
import { parseMeasureIds } from "../utils/measures";
import type {
  CatalogueEntry,
  GeoJSONFeature,
  MeasureConfig,
  RegionConfig,
} from "../types";

interface MeasureDetailsProps {
  measure: GeoJSONFeature | null;
  catalogue: CatalogueEntry[];
  config: RegionConfig;
  onClose: () => void;
}

interface MeasureDetailItem {
  config: MeasureConfig;
  catalogue?: CatalogueEntry;
}

/** Single measure card with expandable "Mehr erfahren" details section. */
const MeasureDetailCard = memo(function MeasureDetailCard({
  item,
  t,
  lang,
}: {
  item: MeasureDetailItem;
  t: (key: string) => string;
  lang: string;
}) {
  const [expanded, setExpanded] = useState(false);
  const description = item.catalogue
    ? lang === "en"
      ? item.catalogue.description_en || item.catalogue.description
      : item.catalogue.description
    : "";
  const details = item.catalogue
    ? lang === "en"
      ? item.catalogue.details_en || item.catalogue.details
      : item.catalogue.details
    : "";
  const hasDetails = Boolean(details);

  return (
    <article className="rounded-lg border border-flima-spruce/10 bg-flima-mist/55 p-3">
      <div className="flex items-start gap-3">
        <span
          className="mt-1 h-3 w-3 shrink-0 rounded-sm"
          style={{ backgroundColor: item.config.color }}
          aria-hidden="true"
        />
        <div className="min-w-0 flex-1">
          <h3 className="break-words font-display text-sm font-semibold text-flima-spruce">
            {item.config.label}
          </h3>
          {item.config.label_with_dates ? (
            <p className="mt-1 break-words font-utility text-[0.65rem] leading-relaxed text-flima-slate/55">
              {t("time_range")}: {item.config.label_with_dates}
            </p>
          ) : null}
          {description ? (
            <p className="mt-2 break-words text-sm leading-relaxed text-flima-slate/75">
              {renderEmphasis(description)}
            </p>
          ) : null}

          {hasDetails ? (
            <div className="mt-2">
              <button
                type="button"
                onClick={() => setExpanded((prev) => !prev)}
                aria-expanded={expanded}
                className="flex min-h-9 items-center gap-1 rounded-md font-utility text-xs font-semibold text-flima-river transition-colors hover:text-flima-spruce focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-flima-river"
              >
                {t("more_info")}
                <svg
                  viewBox="0 0 20 20"
                  className={`h-3.5 w-3.5 transition-transform motion-reduce:transition-none ${expanded ? "rotate-180" : ""}`}
                  fill="currentColor"
                  aria-hidden="true"
                >
                  <path d="m5.5 7.5 4.5 4 4.5-4" />
                </svg>
              </button>
              {expanded ? (
                <div className="mt-2 rounded-md bg-white/70 p-2.5">
                  <p className="break-words text-xs leading-relaxed text-flima-slate/70">
                    {renderEmphasis(details)}
                  </p>
                </div>
              ) : null}
            </div>
          ) : null}
        </div>
      </div>
    </article>
  );
});

export const MeasureDetails = memo(function MeasureDetails({
  measure,
  catalogue,
  config,
  onClose,
}: MeasureDetailsProps) {
  const { t, lang } = useLanguageContext();

  useEffect(() => {
    if (!measure) return;
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [measure, onClose]);

  const measureById = useMemo(
    () => new Map(config.measures.map((item) => [item.id, item])),
    [config.measures],
  );
  const catalogueById = useMemo(
    () => new Map(catalogue.map((item) => [item.measure_id, item])),
    [catalogue],
  );

  const detailItems = useMemo(() => {
    if (!measure) return [];
    // MapLibre stringifies array properties on clicked features, so `measures`
    // arrives as a JSON string here — parseMeasureIds handles either form.
    const ids = parseMeasureIds(measure.properties.measures);
    return ids.reduce<MeasureDetailItem[]>((items, id) => {
      const measureConfig = measureById.get(id);
      if (!measureConfig) return items;
      items.push({ config: measureConfig, catalogue: catalogueById.get(id) });
      return items;
    }, []);
  }, [catalogueById, measure, measureById]);

  if (!measure) return null;

  const properties = measure.properties;
  const overlapType = typeof properties.overlap_type === "string" ? properties.overlap_type : "";
  const color = typeof properties.color === "string" ? properties.color : config.overlap_colors.none;
  const speciesId = typeof properties.species_id === "string" ? properties.species_id : "";
  const location = typeof properties.FUNDORT === "string" ? properties.FUNDORT : "";
  const speciesName =
    speciesId === "both"
      ? t("both_species")
      : speciesId
        ? config.species.find((species) => species.id === speciesId)?.name || speciesId
        : t("standalone_measure");
  const overlapLabels: Record<string, string> = {
    both: t("both_species"),
    bach_only: t("bach_only"),
    helm_only: t("helm_only"),
    none: t("none_affected"),
  };

  return (
    <section
      role="region"
      aria-labelledby="measure-details-title"
      aria-live="polite"
      className="absolute bottom-0 left-0 right-0 z-20 max-h-[72dvh] overflow-y-auto overscroll-contain border-t border-flima-spruce/10 bg-white/95 shadow-2xl backdrop-blur sm:max-h-[55vh]"
    >
      <div className="flima-safe-pb mx-auto flex max-w-5xl gap-4 p-4 sm:gap-5 sm:p-6">
        <span
          className="hidden w-1 shrink-0 rounded-full bg-gradient-to-b from-flima-river to-flima-lichen sm:block"
          aria-hidden="true"
        />
        <div className="min-w-0 flex-1">
          <div className="flex items-start justify-between gap-4">
            <div className="min-w-0">
              <p className="font-utility text-[0.62rem] uppercase tracking-[0.16em] text-flima-river">
                {t("measures")}
              </p>
              <h2
                id="measure-details-title"
                className="mt-1 break-words text-pretty font-display text-xl font-semibold text-flima-spruce"
              >
                {location || speciesName}
              </h2>
              <p className="mt-1 text-sm text-flima-slate/65">
                {t("species_label")}: {speciesName}
              </p>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="flex h-11 w-11 shrink-0 items-center justify-center rounded-md text-2xl leading-none text-flima-slate/55 transition-colors hover:bg-flima-mist hover:text-flima-spruce focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-flima-river focus-visible:ring-offset-2 motion-reduce:transition-none"
              aria-label={t("close")}
            >
              <span aria-hidden="true">×</span>
            </button>
          </div>

          {detailItems.length > 0 ? (
            <div className="mt-5 grid gap-3 sm:grid-cols-2 [content-visibility:auto]">
              {detailItems.map((item) => (
                <MeasureDetailCard key={item.config.id} item={item} t={t} lang={lang} />
              ))}
            </div>
          ) : (
            <p className="mt-4 text-sm text-flima-slate/65">{t("no_data")}</p>
          )}

          {overlapType ? (
            <p className="mt-4 flex items-center gap-2 text-sm font-semibold text-flima-slate/75">
              <span
                className="h-2.5 w-8 rounded-full"
                style={{ backgroundColor: color }}
                aria-hidden="true"
              />
              {overlapLabels[overlapType] || overlapType}
            </p>
          ) : null}
        </div>
      </div>
    </section>
  );
});
